import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { processarFoto, uploadFoto } from "../../lib/image";
import { Eye, EyeOff, Camera, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inputCls =
  "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F5B301] placeholder:text-slate-400";

const PIX_TIPOS = [
  { v: "cpf", l: "CPF" },
  { v: "cnpj", l: "CNPJ" },
  { v: "email", l: "E-mail" },
  { v: "telefone", l: "Telefone" },
  { v: "aleatoria", l: "Chave aleatória" },
];

const onlyDigits = (s) => (s || "").replace(/\D/g, "");

export default function ClubeCadastro() {
  const fileRef = useRef(null);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState("");
  const [form, setForm] = useState({ nome: "", cpf: "", telefone: "", email: "", senha: "", senha2: "" });
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [pixOn, setPixOn] = useState(false);
  const [pix, setPix] = useState({ tipo: "cpf", chave: "", titular_nome: "", titular_doc: "" });
  const [loading, setLoading] = useState(false);
  const [concluido, setConcluido] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pickFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!(f.type || "").startsWith("image/")) return toast.error("Envie um arquivo de imagem (JPG, PNG ou WebP)");
    if (f.size > 10 * 1024 * 1024) return toast.error("Imagem muito grande (máx. 10 MB)");
    setFotoFile(f);
    setFotoPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (form.nome.trim().length < 5) return toast.error("Informe seu nome completo");
    if (onlyDigits(form.cpf).length !== 11) return toast.error("CPF inválido — informe os 11 dígitos");
    if (onlyDigits(form.telefone).length < 10) return toast.error("Telefone/WhatsApp inválido");
    if (!form.email.includes("@")) return toast.error("E-mail inválido");
    if (form.senha.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres");
    if (form.senha !== form.senha2) return toast.error("As senhas não coincidem");
    if (!fotoFile) return toast.error("A foto de perfil é obrigatória");
    setLoading(true);
    try {
      const otimizada = await processarFoto(fotoFile);
      const path = await uploadFoto(otimizada, "indicador_cadastro");
      const payload = { ...form, nome: form.nome.trim(), email: form.email.trim(), foto: path };
      if (pixOn && pix.chave.trim()) payload.pix = pix;
      const r = await api.post("/indicadores/cadastro", payload);
      setConcluido(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Não foi possível concluir o cadastro. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  if (concluido) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="clube-cadastro-sucesso">
        <div className="w-full max-w-sm bg-white rounded-2xl border border-slate-100 shadow-sm p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <h1 className="font-heading text-lg font-bold text-navy">Cadastro enviado!</h1>
          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            Seu cadastro (<strong>{concluido.id_publico}</strong>) está <strong>AGUARDANDO APROVAÇÃO DO ADM</strong>.
            Você receberá um e-mail quando for aprovado e poderá acessar sua carteira e seu link exclusivo de indicação.
          </p>
          <Link to="/clube/login" className="mt-5 inline-block w-full py-3.5 rounded-xl bg-navy text-white font-extrabold text-sm active:scale-95 transition-transform" data-testid="clube-cadastro-ir-login">
            IR PARA O LOGIN
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="clube-cadastro">
      <div className="w-full max-w-md mx-auto">
        <img src="/logo.jpg" alt="Carlos Jr Fretes e Mudanças" className="h-14 w-auto object-contain rounded-xl bg-white p-1 mb-4 mx-auto border border-slate-100" />
        <h1 className="font-heading text-xl font-bold text-navy text-center">Cadastro de Indicador</h1>
        <p className="text-xs text-slate-500 text-center mt-1 mb-5">Após o envio, seu cadastro passa pela aprovação da administração.</p>

        <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4">
          <div className="flex flex-col items-center">
            <button
              type="button"
              data-testid="clube-foto-btn"
              onClick={() => fileRef.current?.click()}
              className="w-24 h-24 rounded-full border-2 border-dashed border-[#F5B301] bg-gold-light/40 flex items-center justify-center overflow-hidden active:scale-95 transition-transform"
            >
              {fotoPreview ? (
                <img src={fotoPreview} alt="Prévia da foto de perfil" className="w-full h-full object-cover" data-testid="clube-foto-preview" />
              ) : (
                <Camera className="w-7 h-7 text-navy" />
              )}
            </button>
            <p className="text-[10px] text-slate-500 mt-1.5 font-semibold uppercase tracking-wide">Foto de perfil (obrigatória)</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="clube-foto-input" onChange={pickFoto} />
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Nome completo *</label>
            <input data-testid="clube-cad-nome" className={`${inputCls} mt-1`} value={form.nome} onChange={set("nome")} placeholder="Seu nome completo" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CPF *</label>
              <input data-testid="clube-cad-cpf" inputMode="numeric" className={`${inputCls} mt-1`} value={form.cpf} onChange={set("cpf")} placeholder="000.000.000-00" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Telefone/WhatsApp *</label>
              <input data-testid="clube-cad-telefone" inputMode="tel" className={`${inputCls} mt-1`} value={form.telefone} onChange={set("telefone")} placeholder="(21) 99999-9999" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">E-mail *</label>
            <input type="email" data-testid="clube-cad-email" className={`${inputCls} mt-1`} value={form.email} onChange={set("email")} placeholder="seu@email.com" autoComplete="email" />
          </div>
          {[
            { k: "senha", label: "Senha *", show: show1, toggle: () => setShow1((s) => !s), tid: "clube-cad-senha" },
            { k: "senha2", label: "Confirmar senha *", show: show2, toggle: () => setShow2((s) => !s), tid: "clube-cad-senha2" },
          ].map((f) => (
            <div key={f.k}>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{f.label}</label>
              <div className="relative mt-1">
                <input
                  type={f.show ? "text" : "password"}
                  data-testid={f.tid}
                  className={`${inputCls} pr-11`}
                  value={form[f.k]}
                  onChange={set(f.k)}
                  placeholder="Mínimo 6 caracteres"
                  autoComplete="new-password"
                />
                <button type="button" data-testid={`${f.tid}-eye`} onClick={f.toggle} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" aria-label={f.show ? "Ocultar senha" : "Mostrar senha"}>
                  {f.show ? <EyeOff className="w-[18px] h-[18px]" /> : <Eye className="w-[18px] h-[18px]" />}
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            data-testid="clube-pix-toggle"
            onClick={() => setPixOn((v) => !v)}
            className="text-xs font-semibold text-navy/70 underline decoration-[#F5B301] decoration-2 underline-offset-2"
          >
            {pixOn ? "Ocultar dados Pix" : "Cadastrar chave Pix agora (opcional — pode fazer depois)"}
          </button>
          {pixOn && (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="clube-pix-panel">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Tipo de chave</label>
                <select data-testid="clube-pix-tipo" className={`${inputCls} mt-1`} value={pix.tipo} onChange={(e) => setPix((p) => ({ ...p, tipo: e.target.value }))}>
                  {PIX_TIPOS.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Chave Pix</label>
                <input data-testid="clube-pix-chave" className={`${inputCls} mt-1`} value={pix.chave} onChange={(e) => setPix((p) => ({ ...p, chave: e.target.value }))} placeholder="Sua chave Pix" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Nome do titular</label>
                <input data-testid="clube-pix-titular" className={`${inputCls} mt-1`} value={pix.titular_nome} onChange={(e) => setPix((p) => ({ ...p, titular_nome: e.target.value }))} placeholder="Titular da conta" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">CPF/CNPJ do titular</label>
                <input data-testid="clube-pix-doc" inputMode="numeric" className={`${inputCls} mt-1`} value={pix.titular_doc} onChange={(e) => setPix((p) => ({ ...p, titular_doc: e.target.value }))} placeholder="Somente números" />
              </div>
            </div>
          )}

          <button
            type="submit"
            data-testid="clube-cadastro-btn"
            disabled={loading}
            className="w-full py-4 rounded-xl bg-[#F5B301] text-navy font-extrabold text-sm flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? "ENVIANDO..." : "ENVIAR CADASTRO PARA APROVAÇÃO"}
          </button>
        </form>
        <Link to="/clube" className="block text-center mt-4 text-xs text-slate-400 underline" data-testid="clube-cadastro-voltar">Voltar</Link>
      </div>
    </div>
  );
}
